import React from 'react';
import { PayoutCalculationStrategy } from 'src/modules/conversions/domain';
import { PayeeType, PayoutScheme, PayoutTermDTO } from 'src/modules/conversions/infra/dtos';
import { useCurrency } from 'src/modules/currencies';
import { useTiersQuery } from 'src/modules/tiers/hooks/useTiersQuery';

import { Skeleton, Text } from '@/design-system/components/ui';
import { maskAddressValue } from '@/utils/format';

import { FormattedAmount } from '../FormattedAmount';

interface Props {
  payoutTerm: PayoutTermDTO;
}

export const PayoutTiers = ({ payoutTerm }: Props) => {
  const {
    scheme,
    calculation_strategy: calculationStrategy,
    payout_currency_address: payoutCurrencyAddress,
    payout_currency_chain_id: payoutCurrencyChainId,
    base_currency: payoutBaseCurrency,
    payout_groups: payoutGroups,
    type,
    pool_amount: poolAmount,
    pool_calculation_day_cron: poolCalculationDayCron,
    pool_duration: poolDuration,
    payee_type: payeeType,
    trigger_amount_source: amountSource,
  } = payoutTerm;

  const isPoolPayout = scheme == PayoutScheme.Pool;
  const isFixedPayment = calculationStrategy === PayoutCalculationStrategy.Fixed;
  const isPointsReward = type === 'point';

  const { data: tiers } = useTiersQuery();

  const { data: currency, isLoading: isCurrencyLoading } = useCurrency(
    isPointsReward ? undefined : payoutCurrencyAddress,
    isPointsReward ? undefined : payoutCurrencyChainId?.toString(),
  );

  const payoutCurrencySymbol = isPointsReward ? 'POINTS' : (currency?.name ?? maskAddressValue(payoutCurrencyAddress));
  const payoutCurrencyDecimals = isPointsReward ? 0 : (currency?.decimals ?? 18);
  const payoutCurrencyLogoUri = isPointsReward ? null : (currency?.logoUri ?? null);

  const hasPayoutBaseCurrency = !!payoutBaseCurrency && payoutBaseCurrency.toLocaleLowerCase() != 'none';
  const payoutBaseCurrencySymbol = hasPayoutBaseCurrency ? payoutBaseCurrency?.toUpperCase() : payoutCurrencySymbol;

  const sortedPayoutGroups = [...(payoutGroups ?? [])].sort((a, b) => {
    const aIsDefault = !a.project_tier;
    const bIsDefault = !b.project_tier;
    if (aIsDefault && !bIsDefault) return -1;
    if (bIsDefault && !aIsDefault) return 1;
    const rankA = a.project_tier?.rank ?? Number.MAX_SAFE_INTEGER;
    const rankB = b.project_tier?.rank ?? Number.MAX_SAFE_INTEGER;
    return rankA - rankB;
  });

  return (
    <div className="tw:flex tw:flex-col tw:gap-6">
      {sortedPayoutGroups?.map((payoutGroup, index) => {
        const referrerAmount =
          isFixedPayment && !isPoolPayout ? payoutGroup.affiliate_amount : payoutGroup.affiliate_amount_percentage;
        const referralAmount =
          isFixedPayment && !isPoolPayout ? payoutGroup.end_user_amount : payoutGroup.end_user_amount_percentage;
        const referrer2Amount =
          isFixedPayment && !isPoolPayout ? payoutGroup.affiliate2_amount : payoutGroup.affiliate2_amount_percentage;
        const referrer3Amount =
          isFixedPayment && !isPoolPayout ? payoutGroup.affiliate3_amount : payoutGroup.affiliate3_amount_percentage;
        const referrer4Amount =
          isFixedPayment && !isPoolPayout ? payoutGroup.affiliate4_amount : payoutGroup.affiliate4_amount_percentage;

        return (
          <div key={payoutGroup.id || index} className="tw:flex tw:flex-col">
            {(() => {
              const tier = payoutGroup.project_tier
                ? (tiers?.find((t) => t.id === payoutGroup.project_tier?.id) ?? null)
                : null;
              const title = payoutGroup.project_tier ? (
                <>
                  <span className="tw:text-muted-foreground">#{payoutGroup.project_tier.rank}</span>{' '}
                  {payoutGroup.project_tier.name}
                </>
              ) : null;
              const description = tier?.description ?? null;
              return (
                <>
                  <Text variant="body-md" weight="bold">
                    {title}
                  </Text>
                  {description && (
                    <Text variant="body-md" className="tw:text-gray-500">
                      {description}
                    </Text>
                  )}
                </>
              );
            })()}

            <div className="tw:flex tw:flex-col tw:gap-[0.8rem] tw:py-[0.2rem] tw:border-l-[0.2rem] tw:border-l-[var(--mui-palette-primary-main)] tw:pl-2">
              {isPoolPayout ? (
                <div className="tw:flex tw:flex-col tw:gap-2">
                  <Text variant="body-md" color="muted">
                    {payeeType === PayeeType.Affiliate ? 'To referrers' : 'To end users'}
                  </Text>
                  {isCurrencyLoading && !isPointsReward ? (
                    <Skeleton width={120} height={20} />
                  ) : (
                    <FormattedAmount
                      {...{
                        amount: poolAmount,
                        userType: 'pool',
                        payoutCurrencyAddress,
                        payoutCurrencyDecimals,
                        payoutCurrencySymbol,
                        payoutCurrencyLogoUri,
                        payoutBaseCurrencySymbol,
                        payoutCurrencyChainId,
                        isFixedPayment: true,
                        isPointsReward,
                        hasPayoutBaseCurrency: !!payoutBaseCurrencySymbol,
                        width: '220px',
                        poolCalculationDay: poolCalculationDayCron as string,
                        poolDuration: poolDuration,
                      }}
                    />
                  )}
                  <Text variant="caption" color="muted">
                    The proportional contribution of each user&apos;s volume/revenue in the calculation will be
                    multiplied by a factor of{' '}
                    {payoutGroup.affiliate_amount_percentage || payoutGroup.end_user_amount_percentage}
                  </Text>
                </div>
              ) : (
                <>
                  {isCurrencyLoading && !isPointsReward ? (
                    <Skeleton width={120} height={20} />
                  ) : (
                    <FormattedAmount
                      {...{
                        amount: referrerAmount,
                        userType: 'affiliate',
                        payoutCurrencyAddress,
                        payoutCurrencyDecimals,
                        payoutCurrencySymbol,
                        payoutCurrencyLogoUri,
                        payoutBaseCurrencySymbol,
                        payoutCurrencyChainId,
                        isFixedPayment,
                        isPointsReward,
                        hasPayoutBaseCurrency: !!payoutBaseCurrencySymbol,
                        width: '220px',
                        amountSource: amountSource ?? undefined,
                        level: referrer2Amount != null ? 1 : undefined,
                      }}
                    />
                  )}
                  {referrer2Amount != null &&
                    (isCurrencyLoading && !isPointsReward ? (
                      <Skeleton width={120} height={20} />
                    ) : (
                      <FormattedAmount
                        {...{
                          amount: referrer2Amount,
                          userType: 'affiliate',
                          payoutCurrencyAddress,
                          payoutCurrencyDecimals,
                          payoutCurrencySymbol,
                          payoutCurrencyLogoUri,
                          payoutBaseCurrencySymbol,
                          payoutCurrencyChainId,
                          isFixedPayment,
                          isPointsReward,
                          hasPayoutBaseCurrency: !!payoutBaseCurrencySymbol,
                          width: '220px',
                          amountSource: amountSource ?? undefined,
                          level: 2,
                        }}
                      />
                    ))}
                  {referrer3Amount != null &&
                    (isCurrencyLoading && !isPointsReward ? (
                      <Skeleton width={120} height={20} />
                    ) : (
                      <FormattedAmount
                        {...{
                          amount: referrer3Amount,
                          userType: 'affiliate',
                          payoutCurrencyAddress,
                          payoutCurrencyDecimals,
                          payoutCurrencySymbol,
                          payoutCurrencyLogoUri,
                          payoutBaseCurrencySymbol,
                          payoutCurrencyChainId,
                          isFixedPayment,
                          isPointsReward,
                          hasPayoutBaseCurrency: !!payoutBaseCurrencySymbol,
                          width: '220px',
                          amountSource: amountSource ?? undefined,
                          level: 3,
                        }}
                      />
                    ))}
                  {referrer4Amount != null &&
                    (isCurrencyLoading && !isPointsReward ? (
                      <Skeleton width={120} height={20} />
                    ) : (
                      <FormattedAmount
                        {...{
                          amount: referrer4Amount,
                          userType: 'affiliate',
                          payoutCurrencyAddress,
                          payoutCurrencyDecimals,
                          payoutCurrencySymbol,
                          payoutCurrencyLogoUri,
                          payoutBaseCurrencySymbol,
                          payoutCurrencyChainId,
                          isFixedPayment,
                          isPointsReward,
                          hasPayoutBaseCurrency: !!payoutBaseCurrencySymbol,
                          width: '220px',
                          amountSource: amountSource ?? undefined,
                          level: 4,
                        }}
                      />
                    ))}
                  {isCurrencyLoading && !isPointsReward ? (
                    <Skeleton width={120} height={20} />
                  ) : (
                    <FormattedAmount
                      {...{
                        amount: referralAmount,
                        userType: 'endUser',
                        payoutCurrencyAddress,
                        payoutCurrencyDecimals,
                        payoutCurrencySymbol,
                        payoutCurrencyLogoUri,
                        payoutBaseCurrencySymbol,
                        payoutCurrencyChainId,
                        isFixedPayment,
                        isPointsReward,
                        hasPayoutBaseCurrency: !!payoutBaseCurrencySymbol,
                        width: '220px',
                        amountSource: amountSource ?? undefined,
                      }}
                    />
                  )}
                </>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};
